/**
 * MyPolicies — employee view of assigned company policies (My Workspace tab).
 * View documents in-app (read-only), acknowledge required policies, see status badges.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  FileText, Eye, CheckCircle2, AlertTriangle, Clock3, Info, Sparkles, RefreshCw
} from 'lucide-react';
import { PolicyViewerDialog } from '../policies/PolicyViewerDialog';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Skeleton } from '../ui/skeleton';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../ui/dialog';
import { toast } from 'sonner';
import { formatDateOnly, formatDateTimeLocal } from '../../utils/formatters';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const fmt = (d) => d ? formatDateOnly(d) : null;

const BADGES = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-100', icon: Sparkles },
  updated: { label: 'Updated — re-acknowledge', cls: 'bg-amber-100 text-amber-700 border-amber-200 hover:bg-amber-100', icon: RefreshCw },
  acknowledged: { label: 'Acknowledged', cls: 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-100', icon: CheckCircle2 },
  info: { label: 'Information only', cls: 'bg-slate-100 text-slate-600 border-slate-200 hover:bg-slate-100', icon: Info },
};

export default function MyPolicies() {
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ackDlg, setAckDlg] = useState(null); // {policy, confirmed}
  const [viewPolicy, setViewPolicy] = useState(null);
  const [saving, setSaving] = useState(false);

  const fetchPolicies = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/policies/my/list`);
      setPolicies(res.data.policies || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load your policies');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const acknowledge = async () => {
    try {
      setSaving(true);
      await axios.post(`${API}/policies/${ackDlg.policy.id}/acknowledge`, {
        version_id: ackDlg.policy.version_id,
        confirmed: true,
      });
      toast.success(`'${ackDlg.policy.title}' acknowledged`);
      setAckDlg(null);
      fetchPolicies();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to acknowledge');
    } finally {
      setSaving(false);
    }
  };

  const pendingCount = policies.filter((p) => ['pending', 'overdue'].includes(p.status)).length;

  if (loading) {
    return <div className="space-y-3">{[1, 2, 3].map((i) => <Skeleton key={i} className="h-24 w-full" />)}</div>;
  }

  return (
    <div className="space-y-4" data-testid="my-policies-tab">
      {policies.length === 0 ? (
        <div className="text-center py-12" data-testid="my-policies-empty">
          <FileText className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500">No policies have been assigned to you yet.</p>
        </div>
      ) : (
        <>
          {pendingCount > 0 && (
            <div className="flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-sm text-amber-800" data-testid="my-policies-pending-banner">
              <AlertTriangle className="h-4 w-4" />
              You have {pendingCount} polic{pendingCount === 1 ? 'y' : 'ies'} awaiting acknowledgment.
            </div>
          )}
          {policies.map((p) => {
            const badge = BADGES[p.badge] || BADGES.new;
            const BadgeIcon = badge.icon;
            const overdue = p.status === 'overdue';
            return (
              <Card key={p.id} className={overdue ? 'border-red-300' : ''} data-testid={`my-policy-card-${p.id}`}>
                <CardContent className="p-4 flex flex-wrap items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${overdue ? 'bg-red-100 text-red-600' : 'bg-blue-50 text-blue-600'}`}>
                    <FileText className="h-5 w-5" />
                  </div>
                  <div className="flex-1 min-w-[240px]">
                    <div className="flex items-center gap-2 flex-wrap">
                      <h3 className="text-sm font-semibold text-slate-900">{p.title}</h3>
                      <Badge className={`text-[11px] ${badge.cls}`}><BadgeIcon className="h-3 w-3 mr-1" />{badge.label}</Badge>
                      {overdue && <Badge className="text-[11px] bg-red-100 text-red-700 border-red-200 hover:bg-red-100"><Clock3 className="h-3 w-3 mr-1" />Overdue</Badge>}
                    </div>
                    {p.description && <p className="text-xs text-slate-500 mt-0.5 line-clamp-2">{p.description}</p>}
                    <p className="text-xs text-slate-400 mt-1">
                      {p.category} · v{p.version_label} · Owner: {p.owner_name || '—'}
                      {p.effective_date && ` · Effective ${fmt(p.effective_date)}`}
                      {p.ack_required && p.due_at && !p.acknowledged_at && (
                        <span className={overdue ? 'text-red-500 font-medium' : ''}> · {overdue ? 'Was due' : 'Acknowledge by'} {fmt(p.due_at)}</span>
                      )}
                      {p.acknowledged_at && ` · Acknowledged ${formatDateTimeLocal(p.acknowledged_at)}`}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-auto">
                    <Button variant="outline" size="sm" onClick={() => setViewPolicy(p)} data-testid={`my-policy-view-${p.id}`}>
                      <Eye className="h-4 w-4 mr-1.5" /> View
                    </Button>
                    {p.ack_required && p.status !== 'acknowledged' && (
                      <Button size="sm" className={overdue ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'}
                        onClick={() => setAckDlg({ policy: p, confirmed: false })}
                        data-testid={`my-policy-ack-btn-${p.id}`}>
                        <CheckCircle2 className="h-4 w-4 mr-1.5" /> Acknowledge
                      </Button>
                    )}
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </>
      )}

      {/* Read-only document viewer */}
      <PolicyViewerDialog policy={viewPolicy} onClose={() => setViewPolicy(null)} />

      {/* Acknowledge dialog */}
      <Dialog open={!!ackDlg} onOpenChange={(o) => { if (!o && !saving) setAckDlg(null); }}>
        <DialogContent data-testid="my-policy-ack-dialog">
          <DialogHeader>
            <DialogTitle>Acknowledge — {ackDlg?.policy?.title}</DialogTitle>
            <DialogDescription>
              Version {ackDlg?.policy?.version_label}. Your acknowledgment is recorded with your name, the
              policy version, date &amp; time.
            </DialogDescription>
          </DialogHeader>
          <label className="flex items-start gap-3 p-3 border border-slate-200 rounded-lg cursor-pointer bg-slate-50">
            <Checkbox
              checked={!!ackDlg?.confirmed}
              onCheckedChange={(v) => setAckDlg((d) => ({ ...d, confirmed: !!v }))}
              data-testid="my-policy-ack-checkbox"
              className="mt-0.5"
            />
            <span className="text-sm text-slate-800 font-medium">I have read and understood this policy.</span>
          </label>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAckDlg(null)} disabled={saving}>Cancel</Button>
            <Button onClick={acknowledge} disabled={saving || !ackDlg?.confirmed}
              className="bg-emerald-600 hover:bg-emerald-700" data-testid="my-policy-ack-confirm-btn">
              {saving ? 'Recording…' : 'Confirm Acknowledgment'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
